import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  initGame,
  destroyGame,
  startGame,
  restartGame,
  downloadScreenshot,
  shareOnTwitter,
  toggleMusic,
  keys,
} from './minigame/gameEngine';
import { submitScore, qualifiesForTop10 } from './minigame/leaderboardApi';
import './Minigame.css';

/**
 * SECCIÓN 4: MINIGAME — Doggy Jump
 * Juego funcional embebido en la landing (Sprint C + E + Security hardening)
 */

/* 🔧 MAINTENANCE MODE — set to false to re-enable the game */
const MAINTENANCE_MODE = false; // false = juego habilitado en producción

// Skip Turnstile on localhost — production site key only works on eldoggy.com
const IS_LOCALHOST =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const TURNSTILE_SITE_KEY = IS_LOCALHOST ? '' : (process.env.REACT_APP_TURNSTILE_SITE_KEY || '');

/* Detect touch-primary device once */
const isTouchDevice =
  typeof window !== 'undefined' &&
  (window.matchMedia('(hover: none) and (pointer: coarse)').matches ||
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0);

/* Recover saved alias from localStorage */
const getSavedAlias = () => {
  try { return localStorage.getItem('doggy_jump_alias') || ''; }
  catch { return ''; }
};

const Minigame = () => {
  const canvasRef = useRef(null);
  const engineReady = useRef(false);
  const sectionRef = useRef(null);

  const [gameState, setGameState] = useState('idle'); // idle | playing | over
  const [score, setScore] = useState(0);
  const [timer, setTimer] = useState('00:30');
  const [endReason, setEndReason] = useState('time');
  const [musicOn, setMusicOn] = useState(true);

  /* ── Leaderboard state (Sprint E) ── */
  const [alias, setAlias] = useState(getSavedAlias);
  const [showAliasInput, setShowAliasInput] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitSaved, setSubmitSaved] = useState(true);   // true = score guardado, false = ya tenías mejor
  const [submitBestScore, setSubmitBestScore] = useState(0); // mejor score real en BD
  const [submitError, setSubmitError] = useState('');
  const [leaderboardKey, setLeaderboardKey] = useState(0);
  const [lastRunMeta, setLastRunMeta] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState(false);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  /* ── Load Turnstile script once ── */
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    if (document.getElementById('cf-turnstile-script')) return;
    const script = document.createElement('script');
    script.id = 'cf-turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  /* ── Render Turnstile widget when alias form is shown ── */
  const renderTurnstileWidget = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current) return;
    // Remove existing widget first
    if (turnstileWidgetId.current !== null && window.turnstile) {
      try { window.turnstile.remove(turnstileWidgetId.current); } catch (_) {}
      turnstileWidgetId.current = null;
    }
    setTurnstileToken('');
    setTurnstileError(false);
    const interval = setInterval(() => {
      if (window.turnstile && turnstileRef.current && turnstileWidgetId.current === null) {
        clearInterval(interval);
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => { setTurnstileToken(token); setTurnstileError(false); },
          'expired-callback': () => setTurnstileToken(''),
          'error-callback': () => { setTurnstileToken(''); setTurnstileError(true); },
          theme: 'dark',
          size: 'compact',
        });
      }
    }, 200);
    return interval;
  }, []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !showAliasInput || submitted) return;
    const interval = renderTurnstileWidget();
    return () => {
      if (interval) clearInterval(interval);
      if (turnstileWidgetId.current !== null && window.turnstile) {
        try { window.turnstile.remove(turnstileWidgetId.current); } catch (_) {}
        turnstileWidgetId.current = null;
      }
      setTurnstileToken('');
      setTurnstileError(false);
    };
  }, [showAliasInput, submitted, renderTurnstileWidget]);

  /* ── Engine lifecycle — lazy init on visibility ── */
  useEffect(() => {
    const section = sectionRef.current;
    const canvas = canvasRef.current;
    if (!section || !canvas) return;

    let initialized = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !initialized) {
          initialized = true;
          observer.disconnect();
          initGame(canvas, {
            onScore: (pts) => setScore(pts),
            onTimer: (str) => setTimer(str),
            onGameOver: async (reason, pts, runMeta) => {
              setEndReason(reason);
              setScore(pts);
              setLastRunMeta(runMeta || null);
              setSubmitted(false);
              setSubmitError('');
              // Check if qualifies for top 3
              const qualifies = await qualifiesForTop10(pts);
              setShowAliasInput(qualifies);
            },
            onGameStart: () => {
              setSubmitted(false);
              setSubmitSaved(true);
              setSubmitBestScore(0);
              setShowAliasInput(false);
              setLastRunMeta(null);
            },
            onStateChange: (state) => setGameState(state),
          }).then(() => {
            engineReady.current = true;
          });
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
      if (engineReady.current) {
        destroyGame();
        engineReady.current = false;
      }
    };
  }, []);

  /* ── Haptic feedback helper ── */
  const vibrate = useCallback((ms = 15) => {
    try { navigator.vibrate?.(ms); } catch (_) {}
  }, []);

  /* ── Touch control helpers ── */
  const touchBind = useCallback((keyName, isJump) => ({
    onTouchStart: (e) => {
      e.preventDefault();
      keys[keyName] = true;
      if (isJump) keys.jumpPressed = true;
      vibrate(isJump ? 25 : 12);
    },
    onTouchEnd: (e) => {
      e.preventDefault();
      keys[keyName] = false;
    },
    onTouchCancel: () => {
      keys[keyName] = false;
    },
  }), [vibrate]);

  /* ── Actions ── */
  const handlePlay = () => startGame();
  const handleRestart = () => {
    setSubmitted(false);    setSubmitSaved(true);
    setSubmitBestScore(0);    setShowAliasInput(false);
    setSubmitError('');
    restartGame();
    startGame();
  };
  const handleDownload = () => downloadScreenshot(score);
  const handleShare = () => shareOnTwitter(score);
  const handleToggleMusic = () => setMusicOn(toggleMusic());

  /* ── Alias / Leaderboard submit ── */
  const handleAliasChange = (e) => {
    let val = e.target.value;
    // Ensure @ prefix
    if (!val.startsWith('@')) val = '@' + val.replace(/@/g, '');
    // Max 16 chars total
    if (val.length > 16) val = val.slice(0, 16);
    // Only allow valid X handle chars: letters, numbers, underscore
    val = '@' + val.slice(1).replace(/[^a-zA-Z0-9_]/g, '');
    setAlias(val);
  };

  const handleSubmitScore = async () => {
    const cleanAlias = alias.trim();
    if (cleanAlias.length < 2) {
      setSubmitError('Escribe tu @ de X (mín. 2 caracteres)');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      localStorage.setItem('doggy_jump_alias', cleanAlias);
    } catch (_) {}
    const result = await submitScore(cleanAlias, score, lastRunMeta, turnstileToken);
    setSubmitting(false);
    if (result.success) {
      setSubmitted(true);
      setSubmitSaved(result.saved !== false);
      setSubmitBestScore(result.bestScore ?? score);
      // Pequeño delay para que Supabase propague la escritura antes de re-fetchar
      setTimeout(() => setLeaderboardKey((k) => k + 1), 1500);
    } else {
      setSubmitError(result.error || 'Error al guardar');
    }
  };

  /* ── Maintenance mode: show banner and stop ── */
  if (MAINTENANCE_MODE) {
    return (
      <section className="minigame section" id="minigame" ref={sectionRef}>
        <div className="container">
          <div className="minigame__content" style={{ textAlign: 'center', padding: '4rem 1rem' }}>
            <span className="minigame__badge">🎮 Doggy Jump</span>
            <h2 className="minigame__title">🚧 EN MANTENIMIENTO 🚧</h2>
            <p className="minigame__subtitle" style={{ fontSize: '1.2rem', marginTop: '1rem' }}>
              El juego está temporalmente en mantenimiento.<br />
              Regresamos pronto, ¡gracias por tu paciencia! 🐶
            </p>
            <a
              href="https://chat.whatsapp.com/IkXLzYfYcWK8Ye1fIlQQI5"
              target="_blank"
              rel="noopener noreferrer"
              className="minigame__telegram-btn"
              style={{ marginTop: '2rem' }}
            >
              💬 Únete a nuestra comunidad de WhatsApp
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="minigame section" id="minigame" ref={sectionRef}>
      <div className="container">
        <div className="minigame__content">
          {/* ── Section header (C.7) ── */}
          <span className="minigame__badge">🎮 Doggy Jump</span>
          <h2 className="minigame__title">Mi Primer $DOGGY</h2>
          <p className="minigame__subtitle">
            Doggy es una cripto que puedes comprar y vender.
          </p>
          <p className="minigame__subtitle">
            Entra al canal de Doggy para conocer a más miembros de la jauría de Doggy
          </p>
          <a
            href="https://chat.whatsapp.com/IkXLzYfYcWK8Ye1fIlQQI5"
            target="_blank"
            rel="noopener noreferrer"
            className="minigame__telegram-btn"
          >
            💬 Entrar a WhatsApp
          </a>

          {/* ── Game wrapper ── */}
          <div className="minigame__game-wrapper">
            {/* HUD */}
            <div className="minigame__hud">
              <span className="minigame__hud-pill">🐕 <span>{score}</span></span>
              <button
                className="minigame__hud-pill minigame__hud-music"
                onClick={handleToggleMusic}
                aria-label="Toggle music"
              >
                {musicOn ? '🔊' : '🔈'}
              </button>
              <span className="minigame__hud-pill minigame__hud-timer">⏱ {timer}</span>
            </div>

            {/* Canvas */}
            <canvas ref={canvasRef} className="minigame__canvas" width={480} height={720} />

            {/* ── Overlay: Start (C.3) ── */}
            {gameState === 'idle' && (
              <div className="minigame__overlay">
                <div className="minigame__panel">
                  <h1 className="minigame__panel-title">🐕 Doggy Jump</h1>
                  <p className="minigame__panel-sub">Salta, recoge monedas y haz el mejor puntaje en 30 segundos</p>
                  <button className="minigame__btn-primary" onClick={handlePlay}>
                    JUGAR
                  </button>
                  {!isTouchDevice && (
                    <p className="minigame__hint-desktop">← → para moverte &nbsp;·&nbsp; ESPACIO para saltar</p>
                  )}
                </div>
              </div>
            )}

            {/* ── Overlay: Game Over (C.4 + E.6) ── */}
            {gameState === 'over' && (
              <div className="minigame__overlay">
                <div className="minigame__panel">
                  <h1 className="minigame__panel-title">
                    {endReason === 'time' ? '⏰ ¡Se acabó el tiempo!' : '💀 Game Over'}
                  </h1>
                  <p className="minigame__score-label">Puntuación</p>
                  <p className="minigame__score-big">{score}</p>

                  {/* ── Alias input for leaderboard (E.6) ── */}
                  {showAliasInput && !submitted && (
                    <div className="minigame__alias-form">
                      <p className="minigame__alias-hint">🏆 ¡Calificas para el Top 3!</p>
                      <div className="minigame__alias-input-wrap">
                        <input
                          type="text"
                          className="minigame__alias-input"
                          value={alias}
                          onChange={handleAliasChange}
                          placeholder="@tu_usuario_de_X"
                          maxLength={16}
                          autoComplete="off"
                          autoCapitalize="off"
                          spellCheck={false}
                        />
                      </div>
                      <button
                        className="minigame__btn-primary minigame__btn-submit"
                        onClick={handleSubmitScore}
                        disabled={submitting || alias.trim().length < 2 || (TURNSTILE_SITE_KEY && !turnstileToken)}
                      >
                        {submitting ? 'Guardando...' : 'Guardar puntaje'}
                      </button>
                      {/* Cloudflare Turnstile CAPTCHA */}
                      {TURNSTILE_SITE_KEY && (
                        <div style={{ marginTop: '8px', textAlign: 'center' }}>
                          <div
                            ref={turnstileRef}
                            className="minigame__turnstile"
                            style={{ display: turnstileError ? 'none' : 'flex', justifyContent: 'center' }}
                          />
                          {turnstileError && (
                            <>
                              <p className="minigame__alias-error" style={{ marginBottom: '4px' }}>⚠️ CAPTCHA no disponible</p>
                              <button
                                className="minigame__btn-ghost"
                                style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                                onClick={() => renderTurnstileWidget()}
                              >
                                🔄 Reintentar
                              </button>
                            </>
                          )}
                        </div>
                      )}
                      {submitError && <p className="minigame__alias-error">{submitError}</p>}
                    </div>
                  )}

                  {/* Score saved confirmation */}
                  {submitted && submitSaved && (
                    <p className="minigame__alias-saved">✅ ¡Puntaje guardado!</p>
                  )}
                  {submitted && !submitSaved && (
                    <p className="minigame__alias-saved" style={{ color: '#F5C542' }}>
                      🏆 Ya tienes {submitBestScore} pts esta semana.<br/>
                      <small style={{ opacity: 0.85 }}>Solo se guarda tu mejor puntaje.</small>
                    </p>
                  )}

                  <div className="minigame__row">
                    <button className="minigame__btn-primary" onClick={handleRestart}>
                      Jugar de nuevo
                    </button>
                    <button className="minigame__btn-ghost" onClick={handleDownload}>
                      📸 Captura
                    </button>
                  </div>
                  <div className="minigame__row">
                    <button className="minigame__btn-share" onClick={handleShare}>
                      Compartir en 𝕏
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Touch controls (C.5) ── */}
          {isTouchDevice && (
            <div className="minigame__touch-controls">
              {/* D-pad: left + right */}
              <div className="minigame__touch-dpad">
                <button
                  className="minigame__touch-btn"
                  {...touchBind('left', false)}
                  aria-label="Move left"
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <button
                  className="minigame__touch-btn"
                  {...touchBind('right', false)}
                  aria-label="Move right"
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
              {/* Jump button — right thumb */}
              <button
                className="minigame__touch-btn minigame__touch-jump"
                {...touchBind('jump', true)}
                aria-label="Jump"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="18 15 12 9 6 15" />
                </svg>
                <span>JUMP</span>
              </button>
            </div>
          )}

          {/* ── WhatsApp CTA (antes del leaderboard) ── */}
          <a
            href="https://chat.whatsapp.com/IkXLzYfYcWK8Ye1fIlQQI5"
            target="_blank"
            rel="noopener noreferrer"
            className="minigame__telegram-btn"
          >
            💬 Entrar a WhatsApp
          </a>



        </div>
      </div>
    </section>
  );
};

export default Minigame;
